import React, { useEffect, useRef } from 'react';
import PersonOutlineRounded from '@material-ui/icons/PersonOutlineRounded';
import AccountBalanceOutlined from '@material-ui/icons/AccountBalanceOutlined';
import RoomOutlined from '@material-ui/icons/RoomOutlined';
import HomeWorkOutlined from '@material-ui/icons/HomeWorkOutlined';
import MyLocationRounded from '@material-ui/icons/MyLocationRounded';
import CompareArrowsRounded from '@material-ui/icons/CompareArrowsRounded';
import ReportProblemOutlined from '@material-ui/icons/ReportProblemOutlined';
import { STRUCTURE_TYPE, FLOOR_NUMBERS } from './FormLoanProperty';
import { inIndia } from '../LoanMaps/mapMeta';

// Read-only check before saving a loan (display only; the save itself is
// unchanged). Add: the key facts plus a map pin for the GPS coordinates.
// Edit: only the fields that changed, before → after.

const EMPTY = '—';
const text = (v) => (v === undefined || v === null || String(v).trim() === '' ? '' : String(v).trim());
const money = (v) => (text(v) ? `₹ ${Number(v).toLocaleString('en-IN')}` : '');
const date = (v) => text(v).slice(0, 10);
const nameOf = (list, v) => list.find((o) => String(o.id) === String(v))?.name || text(v);

// Every field of the form, in form order: [source, key, label, format].
const fields = (loanTypes) => [
  ['loan', 'custFirstName', 'First name'],
  ['loan', 'custLastName', 'Last name'],
  ['loan', 'custMobile', 'Mobile'],
  ['loan', 'custEmail', 'Email'],
  ['loan', 'loanTypeId', 'Loan type', (v) => loanTypes.find((t) => String(t.id) === String(v))?.loanType || text(v)],
  ['loan', 'branchIFSC', 'Branch', (v, row) => text(row.branchName) || text(v)],
  ['loan', 'loanNumber', 'Loan number'],
  ['loan', 'sanctionedAmount', 'Sanctioned amount', money],
  ['loan', 'sanctionedDate', 'Sanctioned date', date],
  ['loan', 'ltv', 'LTV', (v) => (text(v) ? `${v}%` : '')],
  ['loan', 'landValue', 'Land value', money],
  ['loan', 'constructionValue', 'Construction value', money],
  ['loan', 'propertyValue', 'Property value', money],
  ['property', 'structureType', 'Structure type', (v) => nameOf(STRUCTURE_TYPE, v)],
  ['property', 'noOfFloors', 'No of floors', (v) => nameOf(FLOOR_NUMBERS, v)],
  ['property', 'landArea', 'Land area', (v) => (text(v) ? `${v} sq ft` : '')],
  ['property', 'builtUpArea', 'Built-up area', (v) => (text(v) ? `${v} sq ft` : '')],
  ['property', 'propertyDescription', 'Property details'],
  ['property', 'addressLine1', 'Address line 1'],
  ['property', 'addressLine2', 'Address line 2'],
  ['property', 'cityTown', 'City'],
  ['property', 'district', 'District'],
  ['property', 'state', 'State'],
  ['property', 'pincode', 'Pincode'],
  ['property', 'lat', 'Latitude'],
  ['property', 'lng', 'Longitude'],
];

// Fields that differ between the loaded loan and the form.
export function loanChanges({
  loan, property, original, loanTypes,
}) {
  const rows = { loan, property };
  const before = { loan: original.loan || {}, property: original.property || {} };
  return fields(loanTypes)
    .filter(([src, key]) => text(before[src][key]) !== text(rows[src][key]))
    .map(([src, key, label, fmt = text]) => ({
      key,
      label,
      before: fmt(before[src][key], before[src]) || EMPTY,
      after: fmt(rows[src][key], rows[src]) || EMPTY,
    }));
}

// What the coordinates look like: ok, missing, swapped or outside India.
function locationCheck(lat, lng) {
  const la = Number(lat);
  const ln = Number(lng);
  if (!text(lat) || !text(lng) || Number.isNaN(la) || Number.isNaN(ln)) return { kind: 'missing' };
  if (inIndia(la, ln)) return { kind: 'ok', lat: la, lng: ln };
  if (inIndia(ln, la)) return { kind: 'swapped', lat: la, lng: ln };
  return { kind: 'outside', lat: la, lng: ln };
}

const LOCATION_NOTE = {
  missing: 'No GPS coordinates — the loan won\'t show on the map.',
  swapped: 'Latitude and longitude look swapped — the pin is outside India.',
  outside: 'This point is outside India — check the coordinates.',
};

function LocationPin({ lat, lng }) {
  const el = useRef(null);
  const check = locationCheck(lat, lng);

  useEffect(() => {
    const { google } = window;
    if (!google || !el.current || check.kind === 'missing') return;
    const center = { lat: check.lat, lng: check.lng };
    const map = new google.maps.Map(el.current, {
      center,
      zoom: check.kind === 'ok' ? 15 : 3,
      disableDefaultUI: true,
      zoomControl: true,
      clickableIcons: false,
    });
    // eslint-disable-next-line no-new
    new google.maps.Marker({ map, position: center });
  }, [check.kind, check.lat, check.lng]);

  return (
    <div className='lr-location'>
      {check.kind !== 'missing' ? <div ref={el} className='lr-map' /> : null}
      <div className='lr-coords'>
        {check.kind !== 'missing' ? `${text(lat)}, ${text(lng)}` : null}
      </div>
      {LOCATION_NOTE[check.kind] ? (
        <div className={`lr-note ${check.kind}`}>{LOCATION_NOTE[check.kind]}</div>
      ) : null}
    </div>
  );
}

// Why the coordinates matter: the app only accepts site photos and videos
// taken at this location.
function GpsCallout() {
  return (
    <div className='lr-callout' role='note'>
      <ReportProblemOutlined />
      <div>
        <strong>Double-check the coordinates</strong>
        <p>
          If the latitude or longitude is wrong, the customer won&apos;t be able to submit
          site photos and videos. Make sure they match the property.
        </p>
      </div>
    </div>
  );
}

function Card({
  title, icon, children, className = '',
}) {
  return (
    <section className={`lf-section ${className}`}>
      <h3>
        <span className='lf-section-icon'>{icon}</span>
        {title}
      </h3>
      {children}
    </section>
  );
}

function Facts({ items, cols = 3 }) {
  return (
    <dl className={`lr-facts lr-cols-${cols}`}>
      {items.map(([label, value]) => (
        <div key={label}>
          <dt>{label}</dt>
          <dd>{value || EMPTY}</dd>
        </div>
      ))}
    </dl>
  );
}

// Same sections and field order as the form.
export function AddReview({ loan, property, loanTypes }) {
  const f = Object.fromEntries(fields(loanTypes).map(([src, key, , fmt = text]) => [
    key, fmt((src === 'loan' ? loan : property)[key], src === 'loan' ? loan : property),
  ]));

  return (
    <>
      <Card title='Customer' icon={<PersonOutlineRounded />}>
        <Facts
          cols={4}
          items={[
            ['First name', f.custFirstName],
            ['Last name', f.custLastName],
            ['Mobile', f.custMobile],
            ['Email', f.custEmail],
          ]}
        />
      </Card>
      <Card title='Loan' icon={<AccountBalanceOutlined />}>
        <Facts
          items={[
            ['Loan type', f.loanTypeId],
            ['Branch', f.branchIFSC],
            ['Loan number', f.loanNumber],
            ['Sanctioned amount', f.sanctionedAmount],
            ['Sanctioned date', f.sanctionedDate],
            ['LTV', f.ltv],
            ['Land value', f.landValue],
            ['Construction value', f.constructionValue],
            ['Property value', f.propertyValue],
          ]}
        />
      </Card>
      <Card title='Property' icon={<HomeWorkOutlined />}>
        <Facts
          cols={5}
          items={[
            ['Structure type', f.structureType],
            ['No of floors', f.noOfFloors],
            ['Land area', f.landArea],
            ['Built-up area', f.builtUpArea],
            ['Property details', f.propertyDescription],
          ]}
        />
      </Card>
      <Card title='Address & location' icon={<RoomOutlined />}>
        <div className='lr-address'>
          <div className='lr-address-main'>
            <Facts
              cols={2}
              items={[
                ['Address line 1', f.addressLine1],
                ['Address line 2', f.addressLine2],
                ['City', f.cityTown],
                ['District', f.district],
                ['State', f.state],
                ['Pincode', f.pincode],
              ]}
            />
            <GpsCallout />
          </div>
          <div className='lf-geo'>
            <div className='lf-geo-title'>
              <MyLocationRounded />
              GPS coordinates
            </div>
            <LocationPin lat={property.lat} lng={property.lng} />
          </div>
        </div>
      </Card>
    </>
  );
}

export function EditReview({ changes, property }) {
  if (!changes.length) {
    return <p className='lr-empty'>Nothing has changed — there is nothing to save.</p>;
  }
  const moved = changes.some((c) => c.key === 'lat' || c.key === 'lng');
  return (
    <div className={moved ? 'lr-grid' : ''}>
      <Card title={`Changes (${changes.length})`} icon={<CompareArrowsRounded />} className='lr-main'>
        <table className='lr-changes'>
          <thead>
            <tr>
              <th>Field</th>
              <th>Before</th>
              <th>After</th>
            </tr>
          </thead>
          <tbody>
            {changes.map((c) => (
              <tr key={c.key}>
                <td>{c.label}</td>
                <td className='lr-before'>{c.before}</td>
                <td className='lr-after'>{c.after}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
      {moved ? (
        <Card title='New GPS location' icon={<MyLocationRounded />} className='lr-side'>
          <LocationPin lat={property.lat} lng={property.lng} />
          <GpsCallout />
        </Card>
      ) : null}
    </div>
  );
}
